import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { fetchJson } from '../../utils/fetchJson';
import getScopeHeaders from '../../utils/scopeHeaders';
import useScopeAutoRefresh from '../../hooks/useScopeAutoRefresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

const pickDefaultClass = (classes) => {
  if (!Array.isArray(classes) || classes.length === 0) return '';
  const preferred = classes.find((row) => String(row?.level || '').toLowerCase() !== 'nursery');
  return String(preferred?.id || classes[0]?.id || '');
};

const buildEvaluationLabel = (evaluation) => {
  if (!evaluation) return '';
  const base = evaluation.name || `Evaluation ${evaluation.id || ''}`.trim();
  return base;
};

// The shared helper, which resolves the school id as `activeSchoolId` first —
// this copy read the static `schoolId` first, and since the academic year is
// stored under a school-namespaced key, the two disagreed about which year to
// ask for whenever a scope switch had moved `activeSchoolId`.
const getContextHeaders = () => getScopeHeaders();

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatScore = (value) => {
  const numeric = toFiniteNumber(value);
  return numeric == null ? '—' : `${numeric}`;
};

const formatAverage = (value) => {
  const numeric = toFiniteNumber(value);
  return numeric == null ? '—' : numeric.toFixed(2);
};

export default function ClassAcademicPerformancePage() {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [evaluations, setEvaluations] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState('');
  const [matrix, setMatrix] = useState({ classLabel: '', evaluationLabel: '', subjects: [], subjectStatus: [], rows: [] });
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [error, setError] = useState('');
  const [matrixInfo, setMatrixInfo] = useState('');

  const getEvaluationWindowStatus = useCallback((evaluation) => {
    if (!evaluation) return '';
    if (evaluation.type === 'sequence') {
      const sequenceValue = evaluation.sequenceNo || evaluation.number || '';
      const match = (sequences || []).find((row) => String(row?.sequenceNo) === String(sequenceValue));
      return String(match?.status || 'closed');
    }
    return String(evaluation.state || '');
  }, [sequences]);

  const loadClasses = useCallback(async () => {
    try {
      setLoadingClasses(true);
      setError('');
      const response = await fetchJson('/api/teacher/classes?classMasterOnly=1');
      const nextClasses = Array.isArray(response) ? response : [];
      setClasses(nextClasses);
      setSelectedClassId((prev) => {
        if (prev && nextClasses.some((row) => String(row?.id) === String(prev))) return String(prev);
        return pickDefaultClass(nextClasses);
      });
      if (nextClasses.length === 0) {
        setError('No class is assigned to your classmaster profile.');
      }
    } catch (err) {
      console.error('Failed to load classes:', err);
      setClasses([]);
      setSelectedClassId('');
      setError('Failed to load your classmaster class.');
    } finally {
      setLoadingClasses(false);
    }
  }, []);

  useEffect(() => { loadClasses(); }, [loadClasses]);

  // THE YEAR SWITCHER HAS TO REACH THE RESULTS, not just the sequence strip.
  //
  // This page listened for app:ay:changed in exactly one place — the sequence
  // status poll — so switching the year repainted the open/closed chips and
  // left the class list, the evaluation list and the whole results matrix
  // showing the previous session, under the new year's heading. Every figure on
  // screen was then wrong in the most expensive way available: quietly, with a
  // plausible label above it.
  //
  // Reloading the CLASSES is enough and is what the phone does
  // (ClassPerformanceScreen: `onScopeChanged(loadClasses)`): the evaluation
  // effect keys off selectedClassId and the matrix effect off that plus the
  // evaluation, so the whole chain re-runs from the top. A class master may
  // also hold a different class in a different year, which is exactly why the
  // list itself has to be asked again rather than kept.
  useScopeAutoRefresh([loadClasses], [loadClasses]);

  useEffect(() => {
    let active = true;

    const loadSequenceStatuses = async () => {
      try {
        const response = await fetchJson('/api/academics/evaluations/sequences/status', { headers: getContextHeaders() });
        const nextSequences = (response?.sequences || []).map((row) => ({
          sequenceNo: row.sequenceNo || row.number || row.seq || row,
          status: row.status || 'closed',
        }));
        if (!active) return;
        setSequences(nextSequences);
      } catch (_) {
        if (!active) return;
        setSequences([1, 2, 3, 4, 5, 6].map((sequenceNo) => ({ sequenceNo, status: 'closed' })));
      }
    };

    loadSequenceStatuses();
    const onFocus = () => loadSequenceStatuses();
    const onContextChange = () => loadSequenceStatuses();
    const intervalId = window.setInterval(loadSequenceStatuses, 30000);
    window.addEventListener('focus', onFocus);
    window.addEventListener('app:phase:changed', onContextChange);
    window.addEventListener('app:ay:changed', onContextChange);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('app:phase:changed', onContextChange);
      window.removeEventListener('app:ay:changed', onContextChange);
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadEvaluations = async () => {
      if (!selectedClassId) {
        setEvaluations([]);
        setSelectedEvaluationId('');
        setMatrix({ classLabel: '', evaluationLabel: '', subjects: [], subjectStatus: [], rows: [] });
        return;
      }

      try {
        setError('');
        const response = await fetchJson(`/api/academics/evaluations/class/${encodeURIComponent(selectedClassId)}/evaluations`, { headers: getContextHeaders() });
        const nextEvaluations = Array.isArray(response?.evaluations) ? response.evaluations : [];
        if (!active) return;
        setEvaluations(nextEvaluations);
        if (nextEvaluations.length === 0) {
          setMatrix({ classLabel: '', evaluationLabel: '', subjects: [], subjectStatus: [], rows: [] });
        }
      } catch (err) {
        if (!active) return;
        console.error('Failed to load evaluations:', err);
        setEvaluations([]);
        setSelectedEvaluationId('');
        setMatrix({ classLabel: '', evaluationLabel: '', subjects: [], subjectStatus: [], rows: [] });
        setError('Failed to load class evaluations.');
      }
    };

    loadEvaluations();
    return () => {
      active = false;
    };
  }, [selectedClassId]);

  useEffect(() => {
    setSelectedEvaluationId((prev) => {
      if (prev && evaluations.some((row) => String(row?.id) === String(prev))) return String(prev);
      const openEvaluation = evaluations.find((row) => String(getEvaluationWindowStatus(row) || '').toLowerCase() === 'open');
      return String(openEvaluation?.id || evaluations[0]?.id || '');
    });
  }, [evaluations, getEvaluationWindowStatus]);

  useEffect(() => {
    let active = true;

    const loadMatrix = async () => {
      if (!selectedClassId || !selectedEvaluationId) {
        setMatrix({ classLabel: '', evaluationLabel: '', subjects: [], subjectStatus: [], rows: [] });
        setMatrixInfo('');
        return;
      }

      try {
        setLoadingMatrix(true);
        setError('');
        setMatrixInfo('');
        const response = await fetchJson(
          `/api/academics/evaluations/class-results/matrix/classmaster?classId=${encodeURIComponent(selectedClassId)}&evaluationId=${encodeURIComponent(selectedEvaluationId)}`
        );
        if (!active) return;
        const subjects = Array.isArray(response?.subjects) ? response.subjects : [];
        const rows = Array.isArray(response?.rows) ? response.rows : [];
        const hasRecordedMarks = rows.some((row) => Object.values(row?.marks || {}).some((value) => toFiniteNumber(value) != null));
        setMatrix({
          classLabel: response?.classLabel || '',
          evaluationLabel: response?.evaluationLabel || '',
          subjects,
          subjectStatus: Array.isArray(response?.subjectStatus) ? response.subjectStatus : [],
          rows,
        });
        if (!hasRecordedMarks) {
          setMatrixInfo('No marks for this evaluation.');
        }
      } catch (err) {
        if (!active) return;
        console.error('Failed to load class performance matrix:', err);
        const backendMessage = String(err?.data?.error || err?.data?.message || err?.userMessage || err?.message || '').toLowerCase();
        if (err?.response?.status === 404 || backendMessage.includes('no marks')) {
          setMatrix({ classLabel: '', evaluationLabel: '', subjects: [], subjectStatus: [], rows: [] });
          setMatrixInfo('No marks for this evaluation.');
          setError('');
          return;
        }
        setMatrix({ classLabel: '', evaluationLabel: '', subjects: [], subjectStatus: [], rows: [] });
        setMatrixInfo('');
        setError('Failed to load class performance.');
      } finally {
        if (active) setLoadingMatrix(false);
      }
    };

    loadMatrix();
    return () => {
      active = false;
    };
  }, [selectedClassId, selectedEvaluationId]);

  const selectedClass = useMemo(
    () => classes.find((row) => String(row?.id) === String(selectedClassId)) || null,
    [classes, selectedClassId]
  );

  const selectedEvaluation = useMemo(
    () => evaluations.find((row) => String(row?.id) === String(selectedEvaluationId)) || null,
    [evaluations, selectedEvaluationId]
  );

  const displayRows = useMemo(() => {
    const subjects = Array.isArray(matrix.subjects) ? matrix.subjects : [];
    const rows = Array.isArray(matrix.rows) ? matrix.rows : [];

    return rows.map((row) => {
      const scores = subjects
        .map((subject) => toFiniteNumber(row?.marks?.[String(subject.id)]))
        .filter((value) => value != null);
      const total = scores.reduce((sum, value) => sum + value, 0);
      const average = scores.length > 0 ? total / scores.length : null;

      return {
        ...row,
        total,
        average,
        recordedCount: scores.length,
        missingCount: Math.max(subjects.length - scores.length, 0),
      };
    });
  }, [matrix]);

  const summary = useMemo(() => {
    const averages = displayRows.map((row) => row.average).filter((value) => value != null);
    const classAverage = averages.length > 0
      ? averages.reduce((sum, value) => sum + value, 0) / averages.length
      : null;

    return {
      studentCount: displayRows.length,
      subjectCount: Array.isArray(matrix.subjects) ? matrix.subjects.length : 0,
      classAverage,
    };
  }, [displayRows, matrix.subjects]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom>
        Class Performance
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review the current evaluation matrix for your classmaster class.
      </Typography>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <FormControl fullWidth disabled={loadingClasses}>
          <InputLabel id="class-academic-performance-class-label">Class</InputLabel>
          <Select
            labelId="class-academic-performance-class-label"
            value={selectedClassId}
            label="Class"
            onChange={(event) => setSelectedClassId(String(event.target.value))}
          >
            {classes.map((row) => (
              <MenuItem key={row.id} value={String(row.id)}>
                {row.name || row.className || `Class ${row.id}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={!selectedClassId || evaluations.length === 0}>
          <InputLabel id="class-academic-performance-evaluation-label">Evaluation</InputLabel>
          <Select
            labelId="class-academic-performance-evaluation-label"
            value={selectedEvaluationId}
            label="Evaluation"
            onChange={(event) => setSelectedEvaluationId(String(event.target.value))}
          >
            {evaluations.map((row) => (
              <MenuItem key={row.id} value={String(row.id)}>
                {buildEvaluationLabel(row)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {loadingClasses || loadingMatrix ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {!loadingClasses && !loadingMatrix && selectedClass && !selectedEvaluation ? (
        <Alert severity="info">No evaluation is currently available for this class.</Alert>
      ) : null}

      {!loadingClasses && !loadingMatrix && selectedClass && selectedEvaluation ? (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">Class</Typography>
                <Typography variant="body1">{matrix.classLabel || selectedClass?.name || selectedClass?.className || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Evaluation</Typography>
                <Typography variant="body1">{matrix.evaluationLabel || buildEvaluationLabel(selectedEvaluation) || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Students</Typography>
                <Typography variant="body1">{summary.studentCount}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Subjects</Typography>
                <Typography variant="body1">{summary.subjectCount}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Class Average</Typography>
                <Typography variant="body1">{formatAverage(summary.classAverage)}</Typography>
              </Box>
            </Stack>
          </Paper>

          {matrixInfo ? <Alert severity="info">{matrixInfo}</Alert> : null}

          {/* WHOSE SUBJECT IS STILL EMPTY.
              This page is the class master's one view of subjects that are not
              theirs, and it is read-only on purpose: running a class does not
              grant its marks — they enter marks for their own allocated subjects
              like anyone else. What it grants is oversight, and oversight without
              a name attached is half of it. "Physics is blank" is only actionable
              once you know whose it is to fill. */}
          {matrix.subjectStatus.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">Marks entry</Typography>
                <Typography variant="caption" color="text.secondary">
                  {matrix.subjectStatus.filter((x) => x.complete).length}/{matrix.subjectStatus.length} complete
                </Typography>
              </Stack>
              {matrix.subjectStatus.map((st) => (
                <Stack key={st.subjectId} direction="row" alignItems="center" gap={1.5} py={0.75}>
                  {st.complete
                    ? <CheckCircleOutlineIcon fontSize="small" color="success" />
                    : <PendingActionsIcon fontSize="small" color="error" />}
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" noWrap>{st.subjectName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {st.recordedBy.length
                        ? `Entered by ${st.recordedBy.join(', ')}`
                        : 'Nobody has entered these marks'}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {st.filled}/{st.expected}
                  </Typography>
                </Stack>
              ))}
            </Paper>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Roll No</TableCell>
                  <TableCell>Student</TableCell>
                  {matrix.subjects.map((subject) => (
                    <TableCell key={subject.id} align="center">
                      {subject.code || subject.name}
                    </TableCell>
                  ))}
                  <TableCell align="right">Average</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Missing</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.map((row) => (
                  <TableRow key={row.studentId} hover>
                    <TableCell>{row.studentNumber || '—'}</TableCell>
                    <TableCell>{row.studentName || '—'}</TableCell>
                    {matrix.subjects.map((subject) => (
                      <TableCell key={`${row.studentId}-${subject.id}`} align="center">
                        {formatScore(row?.marks?.[String(subject.id)])}
                      </TableCell>
                    ))}
                    <TableCell align="right">{formatAverage(row.average)}</TableCell>
                    <TableCell align="right">{row.recordedCount > 0 ? row.total.toFixed(2) : '—'}</TableCell>
                    <TableCell align="right">{row.missingCount}</TableCell>
                  </TableRow>
                ))}
                {displayRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={matrix.subjects.length + 5} align="center">
                      No marks for this evaluation.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      ) : null}
    </Box>
  );
}
